// Temporary sessionStorage-backed mock for EXR admin seasons until the EXR backend exists. (GMTEC-23994)
// TODO: Replace with real API calls once the EXR season endpoints exist. (GMTEC-23994)
import { SeasonProcessState, SeasonTypeEnum } from '@coin/shared/core/util-enums';
import { PaginatedResult } from '@coin/shared/core/util-models';
import { Season } from '@coin/shared/season-mgmt/util';

type MockSeason = Record<string, unknown>;

const SEASONS_KEY = 'exr-mock-seasons';
const SETTINGS_KEY = 'exr-mock-settings';

export function listExrSeasons(page: number, size: number): PaginatedResult<Season> {
  const all = readSeasons();
  const pageSize = size || all.length || 1;
  const start = page * pageSize;
  return {
    content: all.slice(start, start + pageSize) as unknown as Season[],
    total: all.length,
    currentPage: page + 1,
    pageCount: Math.max(1, Math.ceil(all.length / pageSize)),
    pageSize,
    hasMore: start + pageSize < all.length
  };
}

export function getExrSeason(id: string): Season | null {
  return (readSeasons().find(season => season['id'] === id) as unknown as Season) ?? null;
}

export function createExrSeason(body: MockSeason): MockSeason {
  const now = new Date().toISOString();
  const season: MockSeason = {
    ...body,
    id: body['id'] || `exr-mock-${crypto.randomUUID()}`,
    seasonType: SeasonTypeEnum.ExceptionalRecognitions,
    state: SeasonProcessState.Population, // EXR seasons start in Population (GMTEC-20685 AC)
    assignedTo: null, // null, not the raw id string — the overview's EmployeeNamePipe throws on a primitive
    createdBy: null,
    updatedBy: null,
    createDate: now,
    createdAt: now,
    lastUpdate: now,
    updatedAt: now
  };
  writeSeasons([season, ...readSeasons()]);
  return season;
}

export function updateExrSeason(body: MockSeason): MockSeason {
  const seasons = readSeasons();
  const index = seasons.findIndex(season => season['id'] === body['id']);
  const updated = { ...(index >= 0 ? seasons[index] : {}), ...body, lastUpdate: new Date().toISOString() };
  if (index >= 0) {
    seasons[index] = updated;
  } else {
    seasons.unshift(updated);
  }
  writeSeasons(seasons);
  return updated;
}

// Returns null when absent → SeasonSettingsService maps that to an empty settings entity.
export function getExrSeasonSettings(seasonId: string): MockSeason | null {
  return readSettings()[seasonId] ?? null;
}

export function saveExrSeasonSettings(body: MockSeason): MockSeason {
  const settings = readSettings();
  settings[String(body['seasonId'])] = body;
  writeSettings(settings);
  return body;
}

function readSeasons(): MockSeason[] {
  try {
    return JSON.parse(sessionStorage.getItem(SEASONS_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function writeSeasons(seasons: MockSeason[]): void {
  sessionStorage.setItem(SEASONS_KEY, JSON.stringify(seasons));
}

function readSettings(): Record<string, MockSeason> {
  try {
    return JSON.parse(sessionStorage.getItem(SETTINGS_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function writeSettings(settings: Record<string, MockSeason>): void {
  sessionStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}
